import { useUser } from "@/composables/user/user";
import { createMyItemDetailLink, router } from "@/router";
import { saveAfterAuthRedirect } from "@/services/afterAuthRedirectService";
import { catch401 } from "@/services/apiClient";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import { type CollectibleWithPrice, type Collectible } from "@/services/collectibles/collectiblesTypes";
import type { CollectibleElastic } from "@/services/collectibles/elastic/collectiblesElasticTypes";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import { type CollectionItem, createCollectionItem, type CreateCollectionItemData } from "@/services/collection/item";
import { EventBus, Event } from "@/services/eventBus";
import { factoryImage, type Image } from "@/services/images";
import type { FoilSelection } from "@/services/collectibles/foilVariants";
import { batchSetCollectionItemParameters } from "@/services/parameters/parametersApi";
import { gettext } from "@/services/translation";
import { showErrorToast, showSuccessToast } from "@/services/toasts";

export const getCollectibleImages = (collectible: Collectible): Image[] => {
  return collectible.images.map((imageData) => factoryImage(0, imageData.imageUrl, collectible.name));
};

export const hasPrice = (collectible: Collectible): collectible is CollectibleWithPrice => collectible.price !== null;

export const addIntoCollection = async (collectibleId: CollectibleId, categoryId: CategoryId, redirectToNewlyCreatedItem = true, foil?: FoilSelection): Promise<CollectionItem | void> => {
  const { user } = useUser();
  if (user.value === undefined) {
    const currentLocation = window.location.pathname;
    saveAfterAuthRedirect(currentLocation);
    EventBus.emit({ event: Event.OpenAuthDialog });
  } else {
    const params: CreateCollectionItemData = {
      categoryId,
      collectibleId,
      set: collectibleId,
      minifigure: collectibleId,
      userCollectionListName: "",
    };
    return createCollectionItem(params)
      .then(async (item: CollectionItem) => {
        // Foil tagging is best-effort: the item is already created, so a failure here must not fail the add.
        if (foil !== undefined) {
          await batchSetCollectionItemParameters(item.id, [{ keyId: foil.keyId, valueId: foil.valueId }]).catch((e: unknown) => {
            console.error("Failed to set foil parameter on newly created collection item.", e);
          });
        }
        const msg = gettext.$gettext("Předmět vytvořen.");
        showSuccessToast(msg);
        if (redirectToNewlyCreatedItem) {
          router.push(createMyItemDetailLink(item));
        }
        return item;
      })
      .catch(catch401)
      .catch((e: unknown): void => {
        // @todo better error recognization would be nice
        const msg = gettext.$gettext("Předmět se nepodařilo vytvořit.");
        showErrorToast(msg);
        throw e;
      });
  }
};

/** @deprecated Rework into elastic collectible and its parameters needed.  */
export const getWidgetIdentificator = (collectible: Collectible): string | undefined => {
  const param = collectible.parameters.find((parameter) => "isWidgetIdentificator" in parameter && parameter.isWidgetIdentificator === true);
  return param?.option.value;
};

export const addCollectionItemToCollectible = (collectible: CollectibleElastic, collectionItem: CollectionItem): void => {
  if (collectible.ownedCount === undefined) {
    collectible.ownedCount = collectionItem.pieces;
  } else {
    collectible.ownedCount += collectionItem.pieces;
  }
};
